/**
 * Environment configuration — Zod-validated, fail-fast at app boot.
 *
 * Per AUTAIMATE Build Standard v4 §3 Layer 3 + §23: env vars are
 * validated at module load. A missing or malformed var throws before
 * React mounts, surfacing as a clear error in the browser console and
 * Sentry rather than a cryptic runtime failure deep inside Clerk or
 * the API client.
 *
 * Both vars are set in Cloudflare Pages → Settings → Environment
 * Variables, for Production AND Preview. A push without them will
 * build successfully (Vite doesn't fail on missing VITE_* vars) but
 * the deployed app will throw on first load — which is the correct
 * shape of failure: visible and immediate, not silent and downstream.
 *
 * VITE_CLERK_PUBLISHABLE_KEY
 *   Clerk Frontend SDK key. Starts `pk_test_` for Development instance,
 *   `pk_live_` for Production. Currently wired to the Development
 *   instance (`possible-eagle-41.clerk.accounts.dev`) which matches
 *   the API repo's Clerk wiring. Same instance = interoperable sessions.
 *
 * VITE_API_BASE_URL
 *   Backend Worker base URL. Currently `https://towninsider-api.micks43.workers.dev`
 *   (the workers.dev preview URL). Switches to `https://api.towninsider.co.uk`
 *   when production DNS is wired.
 */

import { z } from 'zod';

const envSchema = z.object({
  VITE_CLERK_PUBLISHABLE_KEY: z
    .string()
    .min(1, 'VITE_CLERK_PUBLISHABLE_KEY is required')
    .startsWith('pk_', {
      message:
        'VITE_CLERK_PUBLISHABLE_KEY must start with "pk_" (set in Cloudflare Pages → Settings → Environment Variables, both Production and Preview)',
    }),
  VITE_API_BASE_URL: z
    .string()
    .min(1, 'VITE_API_BASE_URL is required')
    .url({
      message:
        'VITE_API_BASE_URL must be a valid URL (e.g. https://towninsider-api.micks43.workers.dev)',
    }),
});

const result = envSchema.safeParse(import.meta.env);

if (!result.success) {
  const fieldErrors = result.error.flatten().fieldErrors;
  const summary = Object.entries(fieldErrors)
    .map(([key, messages]) => `  ${key}: ${(messages ?? []).join(', ')}`)
    .join('\n');
  throw new Error(
    `[towninsider-app] Invalid environment configuration:\n${summary}\n\n` +
      `Set the missing vars in Cloudflare Pages → Settings → Environment Variables ` +
      `(both Production and Preview) and redeploy.`,
  );
}

export const env = result.data;
